import { Trash01, UploadCloud02 } from "@untitledui/icons";
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { cx } from "@/utils/cx";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/gif", "image/svg+xml", "image/webp"];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_DIMENSIONS = { width: 800, height: 400 };

type LogoUploaderProps = {
  currentLogoUrl?: string | null;
  onUpload: (file: File) => Promise<void>;
  onDelete: () => Promise<void>;
  isUploading?: boolean;
  isDeleting?: boolean;
};

const validateImageDimensions = (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    if (file.type === "image/svg+xml") {
      resolve(true);
      return;
    }

    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve(img.width <= MAX_DIMENSIONS.width && img.height <= MAX_DIMENSIONS.height);
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      resolve(false);
    };
    img.src = URL.createObjectURL(file);
  });
};

export const LogoUploader = ({ currentLogoUrl, onUpload, onDelete, isUploading, isDeleting }: LogoUploaderProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = useCallback(
    async (file: File) => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error("Invalid file type. Please upload SVG, PNG, JPG, GIF or WebP.");
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast.error("File too large. Maximum size is 5MB.");
        return;
      }

      const validDimensions = await validateImageDimensions(file);
      if (!validDimensions) {
        toast.error(`Image dimensions exceed ${MAX_DIMENSIONS.width}×${MAX_DIMENSIONS.height}px.`);
        return;
      }

      await onUpload(file);
    },
    [onUpload],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      if (file) {
        handleFile(file);
      }
    },
    [handleFile],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFile(file);
      }
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    },
    [handleFile],
  );

  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const isLoading = isUploading || isDeleting;

  return (
    <div className="flex flex-row items-start gap-4 flex-1">
      {currentLogoUrl ? (
        <div className="relative group">
          <img
            src={currentLogoUrl}
            alt="Company logo"
            className="size-12 rounded-lg object-cover shrink-0 ring-1 ring-primary"
          />
          <button
            type="button"
            onClick={onDelete}
            disabled={isLoading}
            className={cx(
              "absolute -top-2 -right-2 size-6 rounded-full bg-error flex items-center justify-center",
              "opacity-0 group-hover:opacity-100 transition-opacity",
              "hover:bg-error/90 disabled:opacity-50 disabled:cursor-not-allowed",
            )}
          >
            <Trash01 className="size-3 text-white" />
          </button>
        </div>
      ) : (
        <div className="size-12 rounded-lg bg-secondary flex items-center justify-center shrink-0 ring-1 ring-primary">
          <span className="text-xs font-medium text-tertiary">Logo</span>
        </div>
      )}

      <div className="flex-1 max-w-md">
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_TYPES.join(",")}
          onChange={handleInputChange}
          className="hidden"
        />
        <button
          type="button"
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          disabled={isLoading}
          className={cx(
            "w-full flex flex-col items-center justify-center gap-2 px-6 py-4 rounded-lg border border-dashed transition-colors",
            isDragging ? "border-brand-600 bg-brand-50/10" : "border-secondary bg-primary",
            isLoading && "opacity-50 cursor-not-allowed",
            !isLoading && "hover:border-brand-600 hover:bg-brand-50/5 cursor-pointer",
          )}
        >
          <div className="size-10 rounded-xl bg-secondary border border-primary flex items-center justify-center">
            <UploadCloud02 className="size-5 text-tertiary" />
          </div>
          <div className="text-center">
            {isUploading ? (
              <span className="text-sm text-tertiary">Uploading...</span>
            ) : isDeleting ? (
              <span className="text-sm text-tertiary">Deleting...</span>
            ) : (
              <>
                <span className="text-sm font-bold text-brand-secondary">Click to upload</span>
                <span className="text-sm text-tertiary"> or drag and drop</span>
              </>
            )}
          </div>
          <p className="text-xs text-tertiary">SVG, PNG, JPG or GIF (max. 800×400px)</p>
        </button>
      </div>
    </div>
  );
};
